import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../assets/logo2 2.png";
import checkTickSrc from "../assets/icons/Check-Tick.png";

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const t = new Date(s);
  return isNaN(t.getTime()) ? null : t;
}

function formatCellDate(value) {
  const d = parseStoredDate(value);
  if (!d) return value ? ampToAnd(String(value)) : "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function ampToAnd(s) {
  if (s == null) return "";
  return String(s).replace(/\s*&\s*/g, " and ");
}

function normalizeImageSource(value, fallbackMime = "image/png") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;
  src = src.replace(/\\\//g, "/").replace(/\\n/g, "").replace(/\\r/g, "");
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  return `data:${fallbackMime};base64,${compact}`;
}

function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  const subtype = m?.[1]?.toLowerCase();
  if (subtype === "png") return "PNG";
  if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
  if (subtype === "webp") return "WEBP";
  return fallbackType;
}

const PLAN_ALERT_OPTIONS = [
  { left: "Extremely Alert", right: "Some signs of Sleepiness" },
  { left: "Very Alert", right: "Sleepy but no efforts to keep awake" },
  { left: "Alert", right: "Sleepy, Some effort to keep awake" },
  { left: "Rather Alert", right: "Very sleepy, great effort keeping awake" },
  { left: "Neither Alert nor Sleepy", right: "" },
];

function loadImageDataUrl(importSrc) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      try {
        const canvas = document.createElement("canvas");
        const scale = 2;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png", 1.0));
      } catch {
        resolve("");
      }
    };
    img.onerror = function () {
      resolve("");
    };
    img.src = importSrc;
  });
}

function drawHseqStyleHeader(doc, pageWidth) {
  const primaryColor = [139, 22, 52];
  const headerHeight = 30;
  doc.setFillColor(...primaryColor);
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.rect(0, 0, pageWidth, headerHeight, "F");

  return new Promise((resolve) => {
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    logoImg.onload = function () {
      try {
        const logoWidth = 70;
        const aspectRatio = logoImg.height / logoImg.width;
        const logoHeight = logoWidth * aspectRatio;
        const logoX = 10;
        const logoY = (headerHeight - logoHeight) / 2;
        const canvas = document.createElement("canvas");
        const scale = 2.5;
        canvas.width = logoImg.width * scale;
        canvas.height = logoImg.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.drawImage(logoImg, 0, 0);
        doc.addImage(canvas.toDataURL("image/png", 1.0), "PNG", logoX, logoY, logoWidth, logoHeight, "", "NONE");
      } catch {
        doc.setFillColor(255, 255, 255);
        doc.rect(10, 6, 60, 15, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("TAYLOR TECHNOLOGY", 15, 12);
        doc.text("SYSTEMS LIMITED", 15, 17);
      }
      resolve();
    };
    logoImg.onerror = function () {
      doc.setFillColor(255, 255, 255);
      doc.rect(10, 6, 60, 15, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text("TAYLOR TECHNOLOGY", 15, 12);
      doc.text("SYSTEMS LIMITED", 15, 17);
      resolve();
    };
    logoImg.src = onsiteproLogo;
  });
}

function applyFooterAllPages(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const footerY = pageHeight - 25;
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.rect(0, footerY, pageWidth, 25, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      20,
      footerY + 8
    );
    doc.text(`Page ${p} of ${totalPages}`, pageWidth - 35, footerY + 15);
  }
}

export async function generateFatigueManagementPlanPdf(record) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const innerW = pageWidth - 20;
  const black = [0, 0, 0];
  const themeFill = [139, 22, 52];
  const themeText = [255, 255, 255];
  const headerHeight = 30;

  await drawHseqStyleHeader(doc, pageWidth);

  const titleX = 90;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text("SF-008-D Fatigue Management Plan", titleX, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);

  let yPosition = headerHeight + 10;

  const tickDataUrl = await loadImageDataUrl(checkTickSrc);

  const tableCommon = {
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.5,
      overflow: "linebreak",
      valign: "top",
      lineColor: black,
      lineWidth: 0.15,
    },
    theme: "grid",
    tableWidth: innerW,
    margin: { left: 10, right: 10, bottom: 28 },
    headStyles: {
      fillColor: themeFill,
      textColor: themeText,
      fontStyle: "bold",
      lineWidth: 0.15,
      lineColor: black,
    },
    bodyStyles: {
      textColor: black,
      lineWidth: 0.15,
      lineColor: black,
    },
  };

  const th = () => ({ fillColor: themeFill, textColor: themeText, fontStyle: "bold" });

  const empDate = formatCellDate(record.date);
  const ap = record.actionPlanAgreement || {};
  const empSigDate = formatCellDate(ap.employeeDate);
  const mgrSigDate = formatCellDate(ap.managerDate);
  const reviewAction = formatCellDate(record.otherComments?.reviewPeriodDate);

  autoTable(doc, {
    ...tableCommon,
    startY: yPosition,
    columnStyles: {
      0: { cellWidth: innerW * 0.25 },
      1: { cellWidth: innerW * 0.25 },
      2: { cellWidth: innerW * 0.25 },
      3: { cellWidth: innerW * 0.25 },
    },
    body: [
      [
        { content: "Employee name", styles: th() },
        { content: "Employee number", styles: th() },
        { content: "Sentinel number (if applicable)", styles: th() },
        { content: "Normal Work location", styles: th() },
      ],
      [
        ampToAnd(record.employeeName || ""),
        ampToAnd(record.employeeNumber || ""),
        ampToAnd(record.sentinelNumber || ""),
        ampToAnd(record.normalWorkLocation || ""),
      ],
      [
        { content: "Job role", styles: th() },
        { content: "Date", styles: th() },
        {
          content: "Name and role of person (or line manager) completing fatigue management plan",
          styles: th(),
          colSpan: 2,
        },
      ],
      [
        ampToAnd(record.jobRole || ""),
        empDate,
        { content: ampToAnd(record.managerNameAndRole || ""), colSpan: 2 },
      ],
      [
        {
          content:
            "Reason for creation of fatigue management plan (trigger type). Include detail on the (potential) causes and impact of the fatigue issue identified:",
          styles: th(),
          colSpan: 4,
        },
      ],
      [{ content: ampToAnd(record.reasonForCreation) || "—", colSpan: 4, styles: { minCellHeight: 14 } }],
    ],
  });
  yPosition = doc.lastAutoTable.finalY + 4;

  const alertVal = String(record.howAlertAreYouFeeling || "").trim();
  const alertBody = PLAN_ALERT_OPTIONS.map((row) => [row.left, "", row.right || "", ""]);

  if (yPosition + 40 > pageHeight - 32) {
    doc.addPage();
    yPosition = 20;
  }

  autoTable(doc, {
    ...tableCommon,
    startY: yPosition,
    columnStyles: {
      0: { cellWidth: innerW * 0.38 },
      1: { cellWidth: innerW * 0.12 },
      2: { cellWidth: innerW * 0.38 },
      3: { cellWidth: innerW * 0.12 },
    },
    body: [
      [{ content: "How alert are you feeling? (Select one)", colSpan: 4, styles: th() }],
      ...alertBody,
    ],
    didDrawCell: (data) => {
      if (data.section !== "body" || !tickDataUrl) return;
      const ri = data.row.index;
      if (ri < 1) return;
      const opt = PLAN_ALERT_OPTIONS[ri - 1];
      if (!opt) return;
      const w = 4;
      const h = 4;
      const cx = data.cell.x + (data.cell.width - w) / 2;
      const cy = data.cell.y + (data.cell.height - h) / 2;
      if (data.column.index === 1 && alertVal === opt.left) {
        try {
          doc.addImage(tickDataUrl, "PNG", cx, cy, w, h, undefined, "NONE");
        } catch {
          /* ignore */
        }
      }
      if (data.column.index === 3 && opt.right && alertVal === opt.right) {
        try {
          doc.addImage(tickDataUrl, "PNG", cx, cy, w, h, undefined, "NONE");
        } catch {
          /* ignore */
        }
      }
    },
  });
  yPosition = doc.lastAutoTable.finalY + 4;

  if (yPosition + 30 > pageHeight - 32) {
    doc.addPage();
    yPosition = 20;
  }

  autoTable(doc, {
    ...tableCommon,
    startY: yPosition,
    body: [
      [
        {
          content:
            "Work status over the course of the fatigue management plan (in work, restricted duties, restricted duties recommended, working hours or duties limitations etc.)",
          colSpan: 4,
          styles: th(),
        },
      ],
      [{ content: ampToAnd(record.workStatusOverPlan) || "—", colSpan: 4, styles: { minCellHeight: 12 } }],
    ],
  });
  yPosition = doc.lastAutoTable.finalY + 6;

  if (yPosition + 50 > pageHeight - 32) {
    doc.addPage();
    yPosition = 20;
  }

  const riskRows = [
    [
      {
        content:
          "Existing work patterns:\n• Discuss working hours and patterns leading to fatigue management plan.\n• Identify any health related or family/home issues that may be considered.",
        styles: { fontStyle: "bold" },
      },
      ampToAnd(record.existingWorkPatterns?.agreedActions) || "—",
      formatCellDate(record.existingWorkPatterns?.reviewPeriodDate),
    ],
    [
      {
        content:
          "Review of control measures/interventions:\n• Discussion about working practices and working hours limits to reduce risk and identify controls to be implemented.",
        styles: { fontStyle: "bold" },
      },
      ampToAnd(record.reviewOfControlMeasures?.agreedActions) || "—",
      formatCellDate(record.reviewOfControlMeasures?.reviewPeriodDate),
    ],
    [
      {
        content:
          "Medical review plans:\n• Discuss any OH recommendations relating to employee (returning to work after absence etc.).",
        styles: { fontStyle: "bold" },
      },
      ampToAnd(record.medicalReviewPlans?.agreedActions) || "—",
      formatCellDate(record.medicalReviewPlans?.reviewPeriodDate),
    ],
    [
      {
        content:
          "Any other comments:\nNote: include detail of any other occupational health management action plan.",
        styles: { fontStyle: "bold" },
      },
      ampToAnd(record.otherComments?.agreedActions) || "—",
      {
        content: "Note: not more than 12 months from the creation of the plan",
        styles: { fontStyle: "italic", fontSize: 7 },
      },
    ],
  ];

  autoTable(doc, {
    ...tableCommon,
    startY: yPosition,
    columnStyles: {
      0: { cellWidth: innerW * 0.35 },
      1: { cellWidth: innerW * 0.5 },
      2: { cellWidth: innerW * 0.15 },
    },
    body: [
      [{ content: "Fatigue Management Plan", colSpan: 3, styles: { ...th(), halign: "center" } }],
      [
        { content: "Risk area and discussion points", styles: th() },
        {
          content: "Agreed action (including accountabilities and responsibilities)",
          styles: th(),
        },
        { content: "Review period/date", styles: { ...th(), halign: "center" } },
      ],
      ...riskRows,
    ],
  });
  yPosition = doc.lastAutoTable.finalY + 6;

  if (yPosition + 70 > pageHeight - 32) {
    doc.addPage();
    yPosition = 20;
  }

  const agreeText =
    "The above action plan has been discussed and agreed. Review dates will be as indicated. Action plan review date:";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  const agreeLines = doc.splitTextToSize(`${agreeText} ${reviewAction || "—"}`, innerW);
  doc.text(agreeLines, 10, yPosition + 4);
  yPosition += 4 + agreeLines.length * 4.8;

  const sigH = 28;
  const colW = innerW / 4;
  const empSrc = normalizeImageSource(ap.employeeSignature, "image/png");
  const mgrSrc = normalizeImageSource(ap.managerSignature, "image/png");

  autoTable(doc, {
    ...tableCommon,
    startY: yPosition,
    columnStyles: {
      0: { cellWidth: colW },
      1: { cellWidth: colW },
      2: { cellWidth: colW },
      3: { cellWidth: colW },
    },
    body: [
      [
        { content: "Signature of employee", styles: th() },
        { content: "", styles: { minCellHeight: sigH } },
        { content: "Signature of person completing fatigue management plan", styles: th() },
        { content: "", styles: { minCellHeight: sigH } },
      ],
      [
        { content: "Date", styles: th() },
        empSigDate,
        { content: "Date", styles: th() },
        mgrSigDate,
      ],
    ],
    didDrawCell: (data) => {
      if (data.section === "body" && data.row.index === 0) {
        const pad = 1;
        if (data.column.index === 1 && empSrc) {
          try {
            const t = getPdfImageType(empSrc, "PNG");
            doc.addImage(
              empSrc,
              t,
              data.cell.x + pad,
              data.cell.y + pad,
              data.cell.width - pad * 2,
              data.cell.height - pad * 2,
              undefined,
              "NONE"
            );
          } catch {
            /* ignore */
          }
        }
        if (data.column.index === 3 && mgrSrc) {
          try {
            const t = getPdfImageType(mgrSrc, "PNG");
            doc.addImage(
              mgrSrc,
              t,
              data.cell.x + pad,
              data.cell.y + pad,
              data.cell.width - pad * 2,
              data.cell.height - pad * 2,
              undefined,
              "NONE"
            );
          } catch {
            /* ignore */
          }
        }
      }
    },
  });
  yPosition = doc.lastAutoTable.finalY + 4;

  if (yPosition + 35 > pageHeight - 32) {
    doc.addPage();
    yPosition = 20;
  }

  const disclaimer =
    "This document forms a record of discussions that have taken place, together with control measures implemented by the company as a safeguard regarding the individual employee's health and wellbeing in relation to fatigue. It should be signed by the line manager and employee, with copies maintained for records and reviewed at agreed dates.";
  const disclaimer2 =
    "This plan must be retained with the employees' employment records for a minimum of three years.";

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  const boxY = yPosition;
  const d1 = doc.splitTextToSize(disclaimer, innerW - 6);
  const d2 = doc.splitTextToSize(disclaimer2, innerW - 6);
  const boxH = 6 + d1.length * 3.8 + 4 + d2.length * 3.8 + 4;
  doc.rect(10, boxY, innerW, boxH);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(d1, 12, boxY + 5);
  doc.text(d2, 12, boxY + 5 + d1.length * 3.8 + 2);

  applyFooterAllPages(doc);

  const safeId = String(record.id || record._id || "plan").replace(/[\\/:"*?<>|]+/g, "-");
  const safeDate = String(empDate || "date").replace(/[\\/:"*?<>|]+/g, "-");
  doc.save(`SF008D_${safeId}_${safeDate}.pdf`);
}
